/**
 * SPEC-TrainingSimulator §5 D5 / §8.4 — the item clock in the Training strip.
 *
 * Mounts the REAL `TimerChip` / `ExtendButton` from `components/qa/*`
 * unchanged, in the same title-row position the live agent page puts them
 * (`qa-detail-v4.tsx`), so the countdown a trainee learns to read in Phase 3 is
 * the one they meet on their first live claim.
 *
 * Behaviour per mode (`clockModeForPhase`):
 *   soft        Phase 2 — the chip shows and records, and nothing blocks. It is
 *               deliberately NOT the live red chip: a soft clock that pulses
 *               like a real expiry would teach the wrong thing, so it stays a
 *               grey "m:ss · target 0:45" chip (§8.4).
 *   extendable  Phase 3 — the live chip, plus "+ Time" once the clock is in its
 *               warning window. At 0:00 the chip reads 0:00 and the item stays
 *               open; "+ Time" is still offered.
 *   hard        Phase 4 — the live chip, no "+ Time". At 0:00 `onTimeout` fires
 *               once; the SERVER decides whether the time really ran out.
 *
 * The countdown here is display only. Everything that matters — elapsed,
 * remaining, whether an extension or a timeout is allowed — is recomputed
 * server-side from `opened_at` on every call (§8.4), so a backgrounded tab, a
 * slept laptop or a hand-set system clock buys the trainee nothing.
 *
 * "+ Time" is offered for the whole extendable item (Sarah, 2026-09-15), so
 * `warningSeconds` only matters to the chip's colour: the live `TimerChip`
 * switches to its warning styling at `TIMER_WARNING_S` (15), which
 * `simulator.warningSeconds` is pinned to (§5.1).
 */

import { useEffect, useRef, useState } from "react";
import { TimerChip, ExtendButton } from "@/components/qa/agent-timer-chip";
import { canExtendNow, formatClock, shouldPostTimeout, tickingRemaining } from "@/lib/simulator-runner-helpers";
import { Starling } from "@/lib/starling-brand";
import type { ClockReading } from "@shared/simulator-helpers";

export interface RunClockProps {
  /** The server's last reading. A new object (a refetch, an extension) restarts the local tick. */
  clock: ClockReading | null;
  warningSeconds: number;
  /** Phase 3 "+ Time". Absent → the button never shows. */
  onExtend?: () => void;
  extending?: boolean;
  /** Phase 4 only, fired once when the local countdown reaches 0:00. */
  onTimeout?: () => void;
}

/** Ticks once a second while a clock is running; returns ms since the reading arrived. */
function useSinceReading(clock: ClockReading | null): number {
  const [now, setNow] = useState(() => Date.now());
  const startedAt = useRef(Date.now());
  useEffect(() => { startedAt.current = Date.now(); setNow(Date.now()); }, [clock]);
  useEffect(() => {
    if (!clock || clock.mode === "none") return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [clock]);
  return now - startedAt.current;
}

export function RunClock({ clock, warningSeconds, onExtend, extending, onTimeout }: RunClockProps) {
  const since = useSinceReading(clock);
  const remaining = tickingRemaining(clock, since);
  const firedFor = useRef<ClockReading | null>(null);

  const timedOut = shouldPostTimeout(clock, since);
  useEffect(() => {
    if (!timedOut || !onTimeout || !clock) return;
    if (firedFor.current === clock) return; // once per reading
    firedFor.current = clock;
    onTimeout();
  }, [timedOut, onTimeout, clock]);

  if (!clock || clock.mode === "none" || remaining === null) return null;

  if (clock.mode === "soft") {
    return (
      <span
        data-testid="run-clock"
        data-mode="soft"
        data-remaining={remaining}
        title="Practice pace — this clock is recorded but never blocks you"
        style={{
          display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 9px", borderRadius: 6,
          fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums", fontFamily: Starling.fontBody,
          background: "#F3F4F6", color: "#6B7280", border: "1px solid #E5E7EB",
        }}
      >
        <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor" }} />
        {formatClock(remaining)} · target {formatClock(clock.allowedSeconds)}
      </span>
    );
  }

  const showExtend = !!onExtend && canExtendNow(clock, since, warningSeconds);
  return (
    <span data-testid="run-clock" data-mode={clock.mode} data-remaining={remaining} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <TimerChip secondsRemaining={remaining} />
      {showExtend && <ExtendButton onExtend={onExtend!} loading={extending} testId="run-clock-extend" />}
    </span>
  );
}

export default RunClock;
